import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

interface ChallanItem {
  idChallanItem: string
  [key: string]: unknown
}

interface OpsChallanItemState {
  requestId: number
  isLoading: boolean
  currentIndex: number
  data: ChallanItem[]
  totalCount: number
  numberOfElements: number
  error: Record<string, unknown>
  channelSource: string
  paginationParams: Record<string, unknown>
  userAssignments: number
  filters: Record<string, unknown>
  searchQuery: string
  delayed: boolean
}

const initialState: OpsChallanItemState = {
  requestId: 0,
  isLoading: false,
  currentIndex: 0,
  data: [],
  totalCount: 0,
  numberOfElements: 0,
  error: {},
  channelSource: '',
  paginationParams: {},
  userAssignments: 0,
  filters: {},
  searchQuery: '',
  delayed: false,
}

const opsChallanItemSlice = createSlice({
  name: 'opsChallanItem',
  initialState,
  reducers: {
    fetchOrdersStart(state, action: PayloadAction<number>) {
      state.isLoading = true
      state.requestId = action.payload
    },
    fetchOrdersEnd(state, action: PayloadAction<number>) {
      if (action.payload !== state.requestId) return
      state.isLoading = false
    },
    fetchDisposeChallanItemsSuccess(
      state,
      action: PayloadAction<{ data: ChallanItem[]; totalElements: number; numberOfElements: number; requestId?: number }>,
    ) {
      if (action.payload.requestId !== undefined && action.payload.requestId !== state.requestId) return
      state.data = action.payload.data
      state.totalCount = action.payload.totalElements
      state.numberOfElements = action.payload.numberOfElements
      state.error = {}
    },
    fetchDisposeChallanItemsFailure(state, action: PayloadAction<Record<string, unknown> & { requestId?: number }>) {
      if (action.payload.requestId !== undefined && action.payload.requestId !== state.requestId) return
      state.data = []
      state.totalCount = 0
      state.numberOfElements = 0
      state.error = action.payload
    },
    resetDisposeChallanItems(state) {
      state.data = []
      state.totalCount = 0
      state.numberOfElements = 0
      state.searchQuery = ''
    },
    refreshDisposeChallanItem(state, action: PayloadAction<ChallanItem>) {
      const index = state.data.findIndex((item) => item.idChallanItem === action.payload.idChallanItem)
      if (index !== -1) {
        state.data[index] = { ...state.data[index], ...action.payload }
      }
    },
    setChallanItemSource(state, action: PayloadAction<string>) {
      state.channelSource = action.payload
    },
    setChallanItemAssignmentType(state, action: PayloadAction<number>) {
      state.userAssignments = action.payload
      state.delayed = false
    },
    setDisposeFilters(state, action: PayloadAction<Record<string, unknown>>) {
      state.filters = action.payload
    },
    setDisposeSearchQuery(state, action: PayloadAction<string>) {
      state.searchQuery = action.payload
    },
    /** Delayed type sets userAssignments to 4 (matching legacy NUMBERS.FOUR) */
    setChallanItemDelayedType(state, action: PayloadAction<boolean>) {
      state.delayed = action.payload
      state.userAssignments = 4
    },
  },
})

export const {
  fetchOrdersStart,
  fetchOrdersEnd,
  fetchDisposeChallanItemsSuccess,
  fetchDisposeChallanItemsFailure,
  resetDisposeChallanItems,
  refreshDisposeChallanItem,
  setChallanItemSource,
  setChallanItemAssignmentType,
  setDisposeFilters,
  setDisposeSearchQuery,
  setChallanItemDelayedType,
} = opsChallanItemSlice.actions

export default opsChallanItemSlice.reducer
